
import { supabase } from "@/lib/supabase";
import { Season } from "@/types/models";
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { Database } from "@/types/supabase";
import { toast } from "sonner";

type SeasonRow = Database['public']['Tables']['seasons']['Row'];
type SeasonInsert = Database['public']['Tables']['seasons']['Insert'];
type SeasonUpdate = Database['public']['Tables']['seasons']['Update'];

export const getSeasons = async (organizationId: string): Promise<Season[]> => {
  const context: ErrorContext = {
    operation: 'getSeasons',
    organizationId
  };

  try {
    console.log("Fetching seasons for organization:", organizationId);
    const { data, error } = await supabase
      .from("seasons")
      .select("*")
      .eq("organization_id", organizationId)
      .order("start_date", { ascending: false });

    if (error) {
      throw error;
    }

    console.log("Seasons fetched successfully:", data);
    return data as Season[];
  } catch (error) {
    handleError(
      error, 
      "Failed to fetch seasons", 
      ErrorSeverity.ERROR, 
      context
    );
    return [];
  }
};

export const getSeason = async (id: string): Promise<Season | null> => {
  const context: ErrorContext = {
    operation: 'getSeason',
    additionalInfo: { seasonId: id }
  };

  try {
    const { data, error } = await supabase
      .from("seasons")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      throw error;
    }

    return data as Season;
  } catch (error) {
    handleError(
      error, 
      "Failed to fetch season details", 
      ErrorSeverity.ERROR, 
      context
    );
    return null;
  }
};

export const createSeason = async (season: Omit<Season, "id" | "created_at">): Promise<Season | null> => {
  const context: ErrorContext = {
    operation: 'createSeason',
    organizationId: season.organization_id
  };

  try {
    const { data, error } = await supabase
      .from("seasons")
      .insert(season as SeasonInsert)
      .select()
      .single();

    if (error) {
      throw error;
    }

    toast.success("Season created successfully");
    return data as Season;
  } catch (error) {
    handleError(
      error, 
      "Failed to create season", 
      ErrorSeverity.ERROR, 
      context
    );
    return null;
  }
};

export const updateSeason = async (id: string, updates: Partial<Season>): Promise<Season | null> => {
  try {
    const { data, error } = await supabase
      .from("seasons")
      .update(updates as SeasonUpdate)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating season:", error);
      toast.error("Failed to update season");
      return null;
    }

    toast.success("Season updated successfully");
    return data as Season;
  } catch (error) {
    console.error("Error in updateSeason:", error);
    toast.error("An unexpected error occurred");
    return null;
  }
};

export const deleteSeason = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("seasons")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting season:", error);
      toast.error("Failed to delete season");
      return false;
    }

    toast.success("Season deleted successfully");
    return true;
  } catch (error) {
    console.error("Error in deleteSeason:", error);
    toast.error("An unexpected error occurred");
    return false;
  }
};

export const setActiveSeason = async (id: string, organizationId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("seasons")
      .update({ is_active: true } as SeasonUpdate)
      .eq("id", id)
      .eq("organization_id", organizationId);

    if (error) {
      console.error("Error setting active season:", error);
      toast.error("Failed to set active season");
      return false;
    }

    toast.success("Season set as active");
    return true;
  } catch (error) {
    console.error("Error in setActiveSeason:", error);
    toast.error("An unexpected error occurred");
    return false;
  }
};

export const toggleSeasonActive = async (id: string, organizationId: string, isActive: boolean): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("seasons")
      .update({ is_active: !isActive } as SeasonUpdate)
      .eq("id", id)
      .eq("organization_id", organizationId);

    if (error) {
      console.error("Error toggling season active status:", error);
      toast.error("Failed to update season status");
      return false;
    }

    toast.success(isActive ? "Season deactivated" : "Season activated");
    return true;
  } catch (error) {
    console.error("Error in toggleSeasonActive:", error);
    toast.error("An unexpected error occurred");
    return false;
  }
};

export const countClassesInSeason = async (seasonId: string): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from("classes")
      .select("*", { count: 'exact', head: true })
      .eq("season_id", seasonId);

    if (error) {
      console.error("Error counting classes in season:", error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error("Error in countClassesInSeason:", error);
    return 0;
  }
};
